/* eslint-env jest */
import {
  getCanvasDimensions, getExportRatio, capExportDimensions, isLargeFormatCustomCategory,
  CUSTOMER_PROOF_MAX_DIM, CUSTOMER_PROOF_MAX_PIXELS, SAFETY_NET_MAX_DIM, SAFETY_NET_MAX_PIXELS,
  interpolateMergeTokens, resolveDisplayText,
} from './canvasUtils';

describe('getCanvasDimensions + getExportRatio round-trip', () => {
  it('exports at native resolution when no scaling_factor is set (legacy/use_new_scaling: false templates)', () => {
    // Every template created via NewTemplatesController#create has
    // use_new_scaling forced to false, so scaling_factor is always nil for
    // these -- the on-screen canvas is shown at full native resolution
    // (factor defaults to 1), so the export ratio must also default to 1
    // to avoid re-multiplying an already-native-resolution canvas.
    const dimensions = getCanvasDimensions(null, 3300, 5100);
    expect(dimensions).toEqual({ width: 3300, height: 5100 });

    const ratio = getExportRatio(undefined, null);
    expect(ratio).toBe(1);
    expect(dimensions.width * ratio).toBe(3300);
    expect(dimensions.height * ratio).toBe(5100);
  });

  it('reconstructs native resolution when scaling_factor shrinks the editing canvas (use_new_scaling: true)', () => {
    const dimensions = getCanvasDimensions(3, 3300, 5100);
    expect(dimensions).toEqual({ width: 1100, height: 1700 });

    const ratio = getExportRatio(undefined, 3);
    expect(ratio).toBe(3);
    expect(dimensions.width * ratio).toBe(3300);
    expect(dimensions.height * ratio).toBe(5100);
  });

  it('an explicit pixelRatio argument overrides the scaling_factor fallback', () => {
    expect(getExportRatio(2, 3)).toBe(2);
    expect(getExportRatio(2, null)).toBe(2);
  });
});

describe('capExportDimensions', () => {
  it('is a no-op when the export is already within both caps (small templates)', () => {
    const result = capExportDimensions(634, 1009, 1, 1600, 2200000);
    expect(result).toEqual({ ratio: 1, width: 634, height: 1009 });
  });

  it('scales a print-resolution poster down to fit maxDim, preserving aspect ratio', () => {
    // A real Halloween Grams poster background: 3600x5400 at ratio 1 (post
    // getExportRatio fix) is 19.4 megapixels -- still needs capping down to
    // a fast, small approval preview.
    const result = capExportDimensions(3600, 5400, 1, 1600, 2200000);
    expect(result.width).toBeLessThanOrEqual(1600);
    expect(result.height).toBeLessThanOrEqual(1600);
    expect(result.width * result.height).toBeLessThanOrEqual(2200000);
    expect(result.width / result.height).toBeCloseTo(3600 / 5400, 5);
    expect(result.ratio).toBeGreaterThan(0);
  });

  it('never floors a sub-1 scale down to a zero-size export', () => {
    // Regression: the original implementation used Math.floor(ratio * scale)
    // to keep pixelRatio an integer. That was harmless while maxDim/maxPixels
    // were huge (scale was always >= 1 for realistic templates), but once
    // capped tightly enough to actually engage for a ratio-1 template, a
    // sub-1 scale (e.g. 1 * 0.3) floors straight to 0 -- a zero-size canvas
    // export. Must stay fractional instead.
    const result = capExportDimensions(3600, 5400, 1, 1600, 2200000);
    expect(result.ratio).toBeGreaterThan(0);
    expect(result.width).toBeGreaterThan(0);
    expect(result.height).toBeGreaterThan(0);
  });

  it('caps on total pixels even when neither side alone exceeds maxDim', () => {
    const result = capExportDimensions(1500, 1500, 1, 1600, 1000000);
    expect(result.width * result.height).toBeLessThanOrEqual(1000000);
    expect(result.width / result.height).toBeCloseTo(1, 5);
  });
});

describe('isLargeFormatCustomCategory', () => {
  it('is true for poster, poster_2 ("Custom"), and grad_folio', () => {
    expect(isLargeFormatCustomCategory('poster')).toBe(true);
    expect(isLargeFormatCustomCategory('poster_2')).toBe(true);
    expect(isLargeFormatCustomCategory('grad_folio')).toBe(true);
  });

  it('is false for every other category, including other physical-print ones', () => {
    expect(isLargeFormatCustomCategory('id_card')).toBe(false);
    expect(isLargeFormatCustomCategory('sitting_cards')).toBe(false);
    expect(isLargeFormatCustomCategory('envelopes')).toBe(false);
    expect(isLargeFormatCustomCategory('flyer')).toBe(false);
    expect(isLargeFormatCustomCategory(undefined)).toBe(false);
    expect(isLargeFormatCustomCategory(null)).toBe(false);
  });
});

describe('cap selection by category (KonvaCanvas export)', () => {
  // custom_template_enabled is a plain admin checkbox with no category
  // restriction, so any physical-print category can reach this export path.
  // Only poster/poster_2/grad_folio have a server-side full-res upgrade
  // after checkout -- every other category's export IS the permanent print
  // file, so it must keep the loose safety-net cap, not the tight preview
  // cap, or its print quality would be silently and permanently degraded.
  it('a large-format category gets the tight preview cap', () => {
    const maxDim = isLargeFormatCustomCategory('poster_2') ? CUSTOMER_PROOF_MAX_DIM : SAFETY_NET_MAX_DIM;
    const maxPixels = isLargeFormatCustomCategory('poster_2') ? CUSTOMER_PROOF_MAX_PIXELS : SAFETY_NET_MAX_PIXELS;

    // A real Halloween Grams / Red Ribbon Week ("Custom") poster background.
    const result = capExportDimensions(3600, 5400, 1, maxDim, maxPixels);
    expect(result.width).toBeLessThan(3600);
    expect(result.height).toBeLessThan(5400);
  });

  it('a same-size non-large-format category (e.g. envelopes) keeps native resolution', () => {
    const maxDim = isLargeFormatCustomCategory('envelopes') ? CUSTOMER_PROOF_MAX_DIM : SAFETY_NET_MAX_DIM;
    const maxPixels = isLargeFormatCustomCategory('envelopes') ? CUSTOMER_PROOF_MAX_PIXELS : SAFETY_NET_MAX_PIXELS;

    // Teacher Envelopes - 12x9 authored at 300 DPI: 3600x2700, 9.7 megapixels
    // -- exceeds CUSTOMER_PROOF_MAX_PIXELS (2.2M) but not the safety net.
    const result = capExportDimensions(3600, 2700, 1, maxDim, maxPixels);
    expect(result).toEqual({ ratio: 1, width: 3600, height: 2700 });
  });
});

// WHICH column types are tokens is decided by Ruby (StaticTextMerge
// .offered_for) and handed to the editor; this only substitutes. A token the
// server did not vouch for must survive untouched, because that is what the
// printed sheet does with it.
describe('interpolateMergeTokens', () => {
  const samples = { first_name: 'John', last_name: 'Smith', grade: '12' };

  it('fills a token with its sample value', () => {
    expect(interpolateMergeTokens("Share {{first_name}}'s smile", samples))
      .toBe("Share John's smile");
  });

  it('fills every token in the string', () => {
    expect(interpolateMergeTokens('{{first_name}} {{last_name}}, grade {{grade}}', samples))
      .toBe('John Smith, grade 12');
  });

  it('tolerates whitespace inside the braces', () => {
    expect(interpolateMergeTokens('Hi {{ first_name }}', samples)).toBe('Hi John');
  });

  it('leaves an unknown token exactly as typed, like the Ruby side', () => {
    expect(interpolateMergeTokens('Share {{frist_name}} today', samples))
      .toBe('Share {{frist_name}} today');
  });

  it('returns text with no tokens unchanged', () => {
    expect(interpolateMergeTokens('Happy Holidays!', samples)).toBe('Happy Holidays!');
  });

  it('does not run away from a stray opening brace', () => {
    expect(interpolateMergeTokens('Save {{ 40% today', samples)).toBe('Save {{ 40% today');
  });

  it('passes empty input straight through', () => {
    expect(interpolateMergeTokens('', samples)).toBe('');
    expect(interpolateMergeTokens(null, samples)).toBe(null);
    expect(interpolateMergeTokens(undefined, samples)).toBe(undefined);
  });

  // The canvas renders before the column fetch resolves; it must draw the raw
  // text then rather than crash or blank the field.
  it('leaves tokens alone when no samples have loaded yet', () => {
    expect(interpolateMergeTokens('Hi {{first_name}}', null)).toBe('Hi {{first_name}}');
  });
});

// The canvas and _generate_id_card.html.erb must resolve the SAME field to the
// same string. The 41 production static-text fields that carry BOTH text and
// sample_text are where that last diverged: the ERB printed text, the canvas
// drew the stale sample, and the token never previewed at all.
describe('resolveDisplayText', () => {
  const samples = { first_name: 'John', grade: '12' };
  const resolve = (field) => resolveDisplayText(field, samples, () => null);

  describe('a Static Text box', () => {
    const staticField = (over) => ({ columnType: 'static_text', label: 'Static Text', ...over });

    it('merges tokens in its text', () => {
      expect(resolve(staticField({ text: "Share {{first_name}}'s smile" })))
        .toBe("Share John's smile");
    });

    it('prefers text over a stale sampleText, like the ERB does', () => {
      expect(resolve(staticField({ text: 'Hi {{first_name}}', sampleText: 'Old sample' })))
        .toBe('Hi John');
    });

    it('falls back to sampleText when text is blank, and merges that too', () => {
      expect(resolve(staticField({ text: '', sampleText: 'Hi {{first_name}}' })))
        .toBe('Hi John');
    });

    it('leaves a token the server did not offer verbatim', () => {
      expect(resolve(staticField({ text: 'Hi {{coupon_code}}' }))).toBe('Hi {{coupon_code}}');
    });

    it('draws the raw token before the samples have loaded', () => {
      expect(resolveDisplayText(staticField({ text: 'Hi {{first_name}}' }), null, () => null))
        .toBe('Hi {{first_name}}');
    });
  });

  describe('every other field type', () => {
    it('keeps its sampleText-first order', () => {
      expect(resolve({ columnType: 'first_name', sampleText: 'Jane', text: 'ignored' }))
        .toBe('Jane');
    });

    it('does not merge -- only static text carries tokens', () => {
      expect(resolve({ columnType: 'first_name', sampleText: '{{first_name}}' }))
        .toBe('{{first_name}}');
    });

    it('falls through to the placeholder, then the label', () => {
      expect(resolve({ columnType: 'grade', defaultPlaceholder: '5' })).toBe('5');
      expect(resolve({ columnType: 'grade', label: 'Grade' })).toBe('Grade');
    });
  });
});
